import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TokenSessionService } from '../../services/token-session.service';

@Component({
  selector: 'app-session-warning',
  template: `
    <div *ngIf="showWarning" class="session-warning-overlay">
      <div class="session-warning-modal">
        <div class="warning-header">
          <i class="dx-icon-warning"></i>
          <h3>Session Expiring Soon</h3>
        </div>
        <div class="warning-content">
          <p>Your session will expire in <strong>{{timeRemaining}}</strong> seconds due to inactivity.</p>
          <p>Click "Stay Logged In" to continue your session, or you will be automatically logged out.</p>
        </div>
        <div class="warning-actions">
          <button class="btn btn-primary" (click)="extendSession()">
            Stay Logged In
          </button>
          <button class="btn btn-secondary" (click)="logout()">
            Logout Now
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .session-warning-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.6);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    }

    .session-warning-modal {
      background: white;
      border-radius: 8px;
      padding: 24px;
      min-width: 400px;
      max-width: 500px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .warning-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      color: #f57c00;
    }

    .warning-header i {
      font-size: 24px;
      margin-right: 12px;
    }

    .warning-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .warning-content {
      margin-bottom: 24px;
      line-height: 1.5;
    }

    .warning-content p {
      margin: 0 0 12px 0;
    }

    .warning-content strong {
      color: #d32f2f;
      font-weight: 600;
    }

    .warning-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }

    .btn {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: background-color 0.2s;
    }

    .btn-primary {
      background-color: #1976d2;
      color: white;
    }

    .btn-primary:hover {
      background-color: #1565c0;
    }

    .btn-secondary {
      background-color: #757575;
      color: white;
    }

    .btn-secondary:hover {
      background-color: #616161;
    }
  `]
})
export class SessionWarningComponent implements OnInit, OnDestroy {
  showWarning = false;
  timeRemaining = 0;
  private destroy$ = new Subject<void>();
  private countdownTimer$ = interval(1000);

  constructor(private tokenSessionService: TokenSessionService) {}

  ngOnInit(): void {
    // Subscribe to session warning state
    this.tokenSessionService.sessionWarning$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(warningState => {
      this.showWarning = warningState.show;
      if (warningState.show && warningState.timeLeft) {
        this.timeRemaining = Math.floor(warningState.timeLeft / 1000);
        this.startCountdown();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startCountdown(): void {
    this.countdownTimer$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.showWarning && this.timeRemaining > 0) {
        this.timeRemaining--;
      }
    });
  }

  extendSession(): void {
    this.tokenSessionService.extendSession();
  }

  logout(): void {
    // Let the service handle the logout
    this.showWarning = false;
    // The TokenSessionService will handle the actual logout
  }
}